import { DEFAULT_CHAIN } from '@/lib/chains'
import { ZORA_TOKEN_CLAIM_CONTRACT_ADDRESS } from '@/lib/contracts'
import { walletClient } from '@/utils/wallet'
import { formatNumber } from '@ourzora/utils/dist'
import { ConnectedWallet } from '@privy-io/react-auth'
import * as Sentry from '@sentry/nextjs'
import { useMutation } from '@tanstack/react-query'
import { zoraTokenCommunityClaimAbi } from '@zoralabs/zora-token-contracts'
import {
  ArrowSquareOut,
  CheckCircle,
  Icon,
  Link,
  Text,
  useToast,
  XCircle,
} from 'reshaped'
import { Address, walletActions } from 'viem'
import { usePublicClient } from 'wagmi'

export function useClaim({
  externalWalletAddress,
  wallet,
  onSuccess,
  tokenAmount = 0,
}: {
  externalWalletAddress: Address
  wallet?: ConnectedWallet
  onSuccess?: () => void
  tokenAmount?: number
}) {
  const toast = useToast()
  const publicClient = usePublicClient()

  return useMutation({
    mutationFn: async () => {
      if (!publicClient) throw new Error('No public client found')
      if (!wallet) throw new Error('No connected wallet found')

      const client = await walletClient({
        chain: DEFAULT_CHAIN,
        account: externalWalletAddress,
        wallet,
      })

      if (!client || !externalWalletAddress)
        throw new Error('No external wallet client found')

      const { request } = await publicClient.simulateContract({
        account: externalWalletAddress,
        abi: zoraTokenCommunityClaimAbi,
        address: ZORA_TOKEN_CLAIM_CONTRACT_ADDRESS,
        functionName: 'claim',
        args: [externalWalletAddress],
      })

      const txHash = await client.extend(walletActions).writeContract(request)

      await publicClient.waitForTransactionReceipt({ hash: txHash })

      return txHash
    },
    onSuccess: (txHash) => {
      // Show success toast
      const explorerUrl = `${DEFAULT_CHAIN.blockExplorers.default.url}/tx/${txHash}`

      toast.show({
        color: 'neutral',
        hideDismiss: true,
        size: 'small',
        timeout: 5000,
        position: 'bottom',
        icon: <Icon svg={CheckCircle} color="positive" size={5} />,
        text: (
          <Text variant="body-3">
            Claimed{' '}
            <Text as="span" weight="medium">
              {formatNumber(Math.floor(tokenAmount))} $ZORA
            </Text>
          </Text>
        ),
        actionsSlot: (
          <Link
            href={explorerUrl}
            variant="plain"
            attributes={{
              target: '_blank',
              rel: 'noopener noreferrer',
              style: {
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              },
            }}
          >
            <Text variant="body-3" weight="regular">
              View on{' '}
              <span style={{ fontWeight: 'var(--rs-font-weight-medium)' }}>Basescan</span>
            </Text>
            <Icon svg={ArrowSquareOut} size={4} />
          </Link>
        ),
      })

      onSuccess?.()
    },
    onError: (error) => {
      const errorMessage = error.message.toLowerCase()
      const isUserRejection = errorMessage.includes('user rejected')

      if (!isUserRejection) {
        // Log to Sentry
        Sentry.captureException(error, {
          tags: {
            walletType: 'eoa',
            action: 'claim',
          },
          extra: {
            externalWalletAddress,
          },
        })

        toast.show({
          text: 'Error Claiming',
          icon: <Icon svg={XCircle} color="critical" size={6} />,
          color: 'neutral',
        })
      }
    },
  })
}
